import { supabase } from '../lib/supabase';

// ─── CATEGORIES ────────────────────────────────────────────────────────────────

export const getCategories = async () => {
  const { data, error } = await supabase?.from('avatar_asset_categories')?.select('*')?.order('layer_order', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const upsertCategory = async (category) => {
  const { data, error } = await supabase?.from('avatar_asset_categories')?.upsert(category, { onConflict: 'slug' })?.select()?.single();
  if (error) throw error;
  return data;
};

// ─── ASSETS ────────────────────────────────────────────────────────────────────

export const getAssets = async ({ categorySlug, search, isActive, isArchived, isStudentVisible, page = 0, pageSize = 50 } = {}) => {
  let query = supabase?.from('avatar_assets')?.select(`
      *,
      avatar_asset_categories (id, slug, label, layer_order)
    `, { count: 'exact' });

  if (categorySlug) {
    const { data: cat } = await supabase?.from('avatar_asset_categories')?.select('id')?.eq('slug', categorySlug)?.single();
    if (cat?.id) query = query?.eq('category_id', cat?.id);
  }

  if (search) {
    query = query?.or(`slug.ilike.%${search}%,filename.ilike.%${search}%,style_variant.ilike.%${search}%`);
  }

  if (isActive !== undefined) query = query?.eq('is_active', isActive);
  if (isArchived !== undefined) query = query?.eq('is_archived', isArchived);
  if (isStudentVisible !== undefined) query = query?.eq('is_student_visible', isStudentVisible);

  query = query?.order('sort_order', { ascending: true })?.order('created_at', { ascending: false })?.range(page * pageSize, (page + 1) * pageSize - 1);

  const { data, error, count } = await query;
  if (error) throw error;
  return { assets: data || [], total: count || 0 };
};

export const getAssetById = async (id) => {
  const { data, error } = await supabase?.from('avatar_assets')?.select(`*, avatar_asset_categories (id, slug, label, layer_order)`)?.eq('id', id)?.single();
  if (error) throw error;
  return data;
};

export const upsertAsset = async (asset) => {
  const { data, error } = await supabase?.from('avatar_assets')?.upsert(asset, { onConflict: 'slug' })?.select()?.single();
  if (error) throw error;
  return data;
};

export const updateAsset = async (id, updates) => {
  const { data, error } = await supabase?.from('avatar_assets')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();
  if (error) throw error;
  return data;
};

export const bulkUpdateAssets = async (ids, updates) => {
  const { data, error } = await supabase?.from('avatar_assets')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.in('id', ids)?.select();
  if (error) throw error;
  return data;
};

export const deleteAsset = async (id) => {
  const { error } = await supabase?.from('avatar_assets')?.delete()?.eq('id', id);
  if (error) throw error;
};

export const deleteAssetWithStorage = async (id, storagePath) => {
  // Delete from storage first (non-fatal if it fails)
  if (storagePath) {
    await supabase?.storage?.from('avatar-assets')?.remove([storagePath]);
  }
  const { error } = await supabase?.from('avatar_assets')?.delete()?.eq('id', id);
  if (error) throw error;
};

export const addAsset = async ({ file, hairBackFile, bodyHeadFile, categoryId, categorySlug, displayName, unlockType, unlockXp, unlockBadgeType, unlockBadgeName, unlockAchievementId, rarity }) => {
  const ext = file?.name?.split('.')?.pop() || 'png';
  const safeName = displayName
    ? displayName?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '_')
    : file?.name?.replace(/\.[^.]+$/, '')?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '_');
  const slug = `${categorySlug}__${safeName}__${Date.now()}`;
  const storagePath = `v1/${categorySlug}/${slug}.${ext}`;

  const { error: storageError } = await supabase?.storage
    ?.from('avatar-assets')
    ?.upload(storagePath, file, { contentType: file?.type || 'image/png', upsert: true });
  if (storageError) throw storageError;

  // Upload hair_back piece if provided (hair_styles category only)
  let hairBackStoragePath = null;
  if (hairBackFile && categorySlug === 'hair_styles') {
    const backExt = hairBackFile?.name?.split('.')?.pop() || 'png';
    hairBackStoragePath = `v1/${categorySlug}/${slug}_back.${backExt}`;
    const { error: backStorageError } = await supabase?.storage
      ?.from('avatar-assets')
      ?.upload(hairBackStoragePath, hairBackFile, { contentType: hairBackFile?.type || 'image/png', upsert: true });
    if (backStorageError) throw backStorageError;
  }

  // Upload body_head piece if provided (bodies category only)
  let bodyHeadStoragePath = null;
  if (bodyHeadFile && categorySlug === 'bodies') {
    const headExt = bodyHeadFile?.name?.split('.')?.pop() || 'png';
    bodyHeadStoragePath = `v1/${categorySlug}/${slug}_head.${headExt}`;
    const { error: headStorageError } = await supabase?.storage
      ?.from('avatar-assets')
      ?.upload(bodyHeadStoragePath, bodyHeadFile, { contentType: bodyHeadFile?.type || 'image/png', upsert: true });
    if (headStorageError) throw headStorageError;
  }

  const { data, error } = await supabase?.from('avatar_assets')?.insert({
    slug,
    filename: displayName ? `${displayName}.${ext}` : file?.name,
    original_filename: file?.name,
    category_id: categoryId,
    storage_bucket: 'avatar-assets',
    storage_path: storagePath,
    hair_back_storage_path: hairBackStoragePath,
    body_head_storage_path: bodyHeadStoragePath,
    mime_type: file?.type || 'image/png',
    is_active: true,
    is_archived: false,
    is_student_visible: true,
    sort_order: 0,
    unlock_requirement_type: unlockType || 'none',
    unlock_xp_required: unlockType === 'xp' ? unlockXp || null : null,
    unlock_badge_type: unlockType === 'badge' ? unlockBadgeType || null : null,
    unlock_badge_name: unlockType === 'badge' ? unlockBadgeName || null : null,
    unlock_achievement_id: unlockType === 'achievement' ? unlockAchievementId || null : null,
    rarity: rarity || 'common',
  })?.select()?.single();
  if (error) throw error;
  return data;
};

// ─── TAGS ──────────────────────────────────────────────────────────────────────

export const getTags = async () => {
  const { data, error } = await supabase?.from('avatar_asset_tags')?.select('*')?.order('name');
  if (error) throw error;
  return data || [];
};

export const upsertTag = async (tag) => {
  const { data, error } = await supabase?.from('avatar_asset_tags')?.upsert(tag, { onConflict: 'slug' })?.select()?.single();
  if (error) throw error;
  return data;
};

export const getAssetTags = async (assetId) => {
  const { data, error } = await supabase?.from('avatar_asset_tag_links')?.select('avatar_asset_tags (*)')?.eq('asset_id', assetId);
  if (error) throw error;
  return data?.map(d => d?.avatar_asset_tags) || [];
};

export const setAssetTags = async (assetId, tagIds) => {
  await supabase?.from('avatar_asset_tag_links')?.delete()?.eq('asset_id', assetId);
  if (tagIds?.length > 0) {
    const links = tagIds?.map(tag_id => ({ asset_id: assetId, tag_id }));
    const { error } = await supabase?.from('avatar_asset_tag_links')?.insert(links);
    if (error) throw error;
  }
};

// ─── STORAGE ───────────────────────────────────────────────────────────────────

export const uploadAssetFile = async (categorySlug, filename, fileBlob, mimeType) => {
  const path = `v1/${categorySlug}/${filename}`;
  const { data, error } = await supabase?.storage?.from('avatar-assets')?.upload(path, fileBlob, {
      contentType: mimeType || 'image/png',
      upsert: true
    });
  if (error) throw error;
  return { path, data };
};

export const getAssetPublicUrl = (storagePath) => {
  const { data } = supabase?.storage?.from('avatar-assets')?.getPublicUrl(storagePath);
  return data?.publicUrl || '';
};

// ─── COLLECTIONS ───────────────────────────────────────────────────────────────

export const getCollections = async () => {
  const { data, error } = await supabase?.from('avatar_asset_collections')?.select('*')?.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const upsertCollection = async (collection) => {
  const { data, error } = await supabase?.from('avatar_asset_collections')?.upsert(collection, { onConflict: 'slug' })?.select()?.single();
  if (error) throw error;
  return data;
};

export const deleteCollection = async (id) => {
  const { error } = await supabase?.from('avatar_asset_collections')?.delete()?.eq('id', id);
  if (error) throw error;
};

// ─── PRESETS ───────────────────────────────────────────────────────────────────

export const getPresets = async (studentVisibleOnly = false) => {
  let query = supabase?.from('avatar_presets')?.select('*')?.order('created_at', { ascending: false });
  if (studentVisibleOnly) query = query?.eq('is_student_visible', true);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const upsertPreset = async (preset) => {
  const { data, error } = await supabase?.from('avatar_presets')?.upsert(preset, { onConflict: 'slug' })?.select()?.single();
  if (error) throw error;
  return data;
};

export const deletePreset = async (id) => {
  const { error } = await supabase?.from('avatar_presets')?.delete()?.eq('id', id);
  if (error) throw error;
};
